import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Trash2, ShieldCheck, X } from 'lucide-react';

// ── Estado global del diálogo ─────────────────────────────────────────────────
let openDialog = null;

export function useConfirm() {
  return useCallback((options) => {
    return new Promise((resolve) => {
      if (openDialog) openDialog({ ...options, resolve });
    });
  }, []);
}

// ── Variantes ─────────────────────────────────────────────────────────────────
const VARIANTS = {
  danger: {
    icon: Trash2,
    iconBg: '#fef2f2',
    iconColor: '#dc2626',
    confirmClass: 'btn-danger',
    confirmLabel: 'Eliminar',
  },
  warning: {
    icon: AlertTriangle,
    iconBg: '#fffbeb',
    iconColor: '#d97706',
    confirmClass: 'btn-warning',
    confirmLabel: 'Confirmar',
  },
  confirm: {
    icon: ShieldCheck,
    iconBg: '#eff6ff',
    iconColor: '#2563eb',
    confirmClass: 'btn-primary',
    confirmLabel: 'Confirmar',
  },
};

// ── Componente diálogo ────────────────────────────────────────────────────────
function Dialog({ title, message, confirmLabel, cancelLabel, variant, resolve, onDone }) {
  const [visible, setVisible] = useState(false);
  const confirmRef = useRef(null);
  const cfg = VARIANTS[variant] || VARIANTS.confirm;
  const Icon = cfg.icon;

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    confirmRef.current?.focus();

    const handler = (e) => { if (e.key === 'Escape') dismiss(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const dismiss = (result) => {
    setVisible(false);
    setTimeout(() => { resolve(result); onDone(); }, 220);
  };

  return createPortal(
    <div
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(false); }}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9998, padding: 20,
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.2s ease',
      }}
    >
      <div style={{
        background: 'white',
        borderRadius: 16,
        boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 4px 16px rgba(0,0,0,0.08)',
        width: '100%',
        maxWidth: 400,
        overflow: 'hidden',
        transform: visible ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(8px)',
        transition: 'transform 0.22s cubic-bezier(0.34,1.4,0.64,1)',
      }}>
        {/* Header */}
        <div style={{ padding: '24px 24px 0', display: 'flex', alignItems: 'flex-start', gap: 16 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, flexShrink: 0,
            background: cfg.iconBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon size={22} color={cfg.iconColor} strokeWidth={2} />
          </div>
          <div style={{ flex: 1, paddingTop: 2 }}>
            <div style={{ fontWeight: 600, fontSize: 16, color: '#111827', lineHeight: 1.3 }}>
              {title}
            </div>
            {message && (
              <div style={{ fontSize: 14, color: '#6b7280', marginTop: 6, lineHeight: 1.5 }}>
                {message}
              </div>
            )}
          </div>
          <button
            onClick={() => dismiss(false)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#9ca3af', padding: 2, marginTop: -2, marginRight: -4,
              display: 'flex', alignItems: 'center', borderRadius: 6,
              transition: 'color 0.15s, background 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#374151'; e.currentTarget.style.background = '#f3f4f6'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#9ca3af'; e.currentTarget.style.background = 'none'; }}
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', gap: 10, justifyContent: 'flex-end',
          padding: '20px 24px 24px',
        }}>
          <button
            onClick={() => dismiss(false)}
            className="btn btn-secondary"
            style={{ minWidth: 80 }}
          >
            {cancelLabel || 'Cancelar'}
          </button>
          <button
            ref={confirmRef}
            onClick={() => dismiss(true)}
            className={`btn ${cfg.confirmClass}`}
            style={{ minWidth: 100 }}
          >
            {confirmLabel || cfg.confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Contenedor global (se monta en App) ───────────────────────────────────────
export function ConfirmContainer() {
  const [dialog, setDialog] = useState(null);

  useEffect(() => {
    openDialog = (opts) => setDialog(opts);
    return () => { openDialog = null; };
  }, []);

  if (!dialog) return null;

  return (
    <Dialog
      {...dialog}
      onDone={() => setDialog(null)}
    />
  );
}
